/**
 * admin/makua/AdminCourseLibrary.js — the courses Forge shares, and which
 * Makua guests get them (spec 08.2).
 *
 * Courses are written once, in Forge's admin, and ticked "Also offer to
 * Makua" there. Here Makua's team decides who gets each one: every guest
 * with a confirmed stay, a retreat type, one date, a cohort or a person,
 * when it opens (straight away, before arrival, after departure), and
 * whether it waits for medical clearance. Guests' progress stays here.
 */
import { useEffect, useMemo, useState } from "react";
import { HiOutlineAcademicCap } from "react-icons/hi2";
import AdminLayout from "../AdminLayout";
import { courseAssignmentsAPI } from "../../services/api";
import { useCurrentAdmin, hasPermission } from "../permissions";
import CourseRules from "../courses/CourseRules";
import Picture from "../../components/utils/Picture";
import { useLoad } from "./ops";
import "./ops.css";
import "./AdminCourseLibrary.css";

export default function AdminCourseLibrary() {
  const { admin } = useCurrentAdmin();
  const canEdit = hasPermission(admin, "courses.edit");
  const { data, loading, error, reload } = useLoad(() => courseAssignmentsAPI.library(), []);
  const courses = useMemo(() => data?.courses || [], [data]);
  const [openId, setOpenId] = useState(null);
  useEffect(() => { if (!openId && courses.length) setOpenId(courses[0].id); }, [courses, openId]);
  const open = courses.find((c) => c.id === openId);

  return (
    <AdminLayout pageTitle="Courses" pageSubtitle="Preparation and integration courses from the shared library, and which guests get them." permission="courses.view" feature="courseLibrary">
      <div className="ops">
        {loading && !data && <p className="ops-empty">Loading the library…</p>}
        {error && (
          <div className="ops-card"><p className="ops-error">{error}</p><button className="ops-btn" onClick={reload}>Try again</button></div>
        )}
        {data && !data.connected && (
          <p className="ops-note">
            The course library isn't connected. On this backend set <code>FORGE_API_URL</code> (Forge's API address, ending in /api)
            and <code>SHARED_API_KEY</code> (the same value as on Forge's backend).
          </p>
        )}
        {data?.connected && courses.length === 0 && (
          <div className="ops-card ops-empty">
            <HiOutlineAcademicCap className="mcl__empty-icon" aria-hidden="true" />
            <p>No courses are shared with Makua yet. In Forge's admin, open a course, then Settings → "Also offer to Makua", and publish it.</p>
          </div>
        )}
        {courses.length > 0 && (
          <div className="mcl">
            <nav className="mcl__list" aria-label="Courses">
              {courses.map((c) => (
                <button key={c.id} type="button" className={`mcl__item ${c.id === openId ? "is-on" : ""}`} aria-current={c.id === openId ? "true" : undefined} onClick={() => setOpenId(c.id)}>
                  <span className="mcl__thumb">
                    {c.thumbnail ? <Picture src={c.thumbnail} alt="" width={96} height={64} sizes="96px" /> : <HiOutlineAcademicCap aria-hidden="true" />}
                  </span>
                  <span className="mcl__item-text">
                    <span className="mcl__item-title">{c.title}</span>
                    <span className="mcl__item-meta">{c.lesson_count} lessons · {c.rules ? `${c.rules} ${c.rules === 1 ? "rule" : "rules"}` : "not given to anyone"}</span>
                  </span>
                </button>
              ))}
            </nav>
            {open && <CourseDetail key={open.id} course={open} canEdit={canEdit} onChanged={reload} />}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function CourseDetail({ course, canEdit, onChanged }) {
  return (
    <div className="mcl__detail">
      <header className="ops-card mcl__head">
        <h2 className="mcl__title">{course.title}</h2>
        {course.description && <p className="mcl__desc">{course.description}</p>}
        <p className="mcl__meta">
          {course.chapter_count} {course.chapter_count === 1 ? "chapter" : "chapters"} · {course.lesson_count} lessons
          {course.required_count > 0 && course.required_count < course.lesson_count ? ` (${course.required_count} required)` : ""}
          {" · "}Written and kept up to date in Forge's admin
        </p>
      </header>
      <CourseRules
        courseId={course.id}
        canEdit={canEdit}
        onChange={onChanged}
        intro="Only guests with a confirmed (paid) stay get courses through a retreat type, date, cohort or 'everyone'. A course for one person reaches them by email, booking or not."
      />
    </div>
  );
}
